import React from 'react';
import {
    Select as AntdSelect,
    type SelectProps as AntdSelectProps,
    type RefSelectProps,
} from 'antd';

/**
 * Option shape accepted by the native `SearchSelect`.
 *
 * Mirrors the legacy `@relo/sports-components` SearchSelect option contract
 * (`{ name, value }`) so existing app usages migrate with behavior parity.
 * `name` is the human-readable, searchable label; `value` is the option key.
 */
export type SearchSelectOption<ValueType extends string | number> = {
    name: string;
    value: ValueType;
    disabled?: boolean;
};

type SearchSelectBaseProps<ValueType extends string | number> = Omit<
    AntdSelectProps<ValueType | ValueType[]>,
    | 'options'
    | 'onChange'
    | 'showSearch'
    | 'filterOption'
    | 'mode'
    | 'size'
    | 'value'
> & {
    /** Searchable options in the legacy `{ name, value }` shape. */
    options?: SearchSelectOption<ValueType>[];
    /** Loading spinner in the dropdown while options resolve. */
    loading?: boolean;
    /** Hides the clear (x) affordance for required single-select usage. */
    hideClearButton?: boolean;
    /** Truncates the displayed selected label to this many characters. */
    displayValueMaxLength?: number;
    /** Legacy compact sizing; `sm` maps to AntD `small`. */
    size?: 'sm' | AntdSelectProps['size'];
    /** Legacy form field identifier, preserved for migration parity. */
    name?: string;
};

export type SearchSelectSingleProps<ValueType extends string | number> =
    SearchSelectBaseProps<ValueType> & {
        mode?: undefined;
        value?: ValueType | null;
        onChange?: (
            value: ValueType | null,
            option?: SearchSelectOption<ValueType>,
        ) => void;
    };

export type SearchSelectMultipleProps<ValueType extends string | number> =
    SearchSelectBaseProps<ValueType> & {
        mode: 'multiple';
        value?: ValueType[];
        onChange?: (
            value: ValueType[],
            options?: SearchSelectOption<ValueType>[],
        ) => void;
    };

export type SearchSelectProps<ValueType extends string | number> =
    | SearchSelectSingleProps<ValueType>
    | SearchSelectMultipleProps<ValueType>;

type SearchSelectComponent = {
    <ValueType extends string | number = number>(
        props: SearchSelectMultipleProps<ValueType> & {
            ref?: React.Ref<RefSelectProps>;
        },
    ): React.ReactElement;
    <ValueType extends string | number = number>(
        props: SearchSelectSingleProps<ValueType> & {
            ref?: React.Ref<RefSelectProps>;
        },
    ): React.ReactElement;
};

const legacySizeMap: Record<string, AntdSelectProps['size']> = {
    sm: 'small',
};

const includesStr = (label: string, term: string) =>
    label.toLowerCase().includes(term.toLowerCase());

const SearchSelectInner = <ValueType extends string | number>(
    {
        options = [],
        value,
        onChange,
        loading,
        hideClearButton = false,
        displayValueMaxLength,
        mode,
        size,
        // `name` is a legacy decorative field identifier; preserved on the props
        // contract for migration parity but not forwarded to the raw AntD Select.
        name: _name,
        placeholder = 'Choose an option ...',
        ...props
    }: SearchSelectProps<ValueType>,
    ref: React.Ref<RefSelectProps>,
) => {
    const antdOptions = options.map(option => {
        const label =
            displayValueMaxLength && option.name.length > displayValueMaxLength
                ? option.name.substring(0, displayValueMaxLength)
                : option.name;

        return {
            label,
            value: option.value,
            disabled: option.disabled,
        };
    });

    const handleChange = (
        nextValue: ValueType | ValueType[] | undefined,
    ): void => {
        if (mode === 'multiple') {
            const nextValues = Array.isArray(nextValue) ? nextValue : [];
            const selected = options.filter(option =>
                nextValues.includes(option.value),
            );
            (onChange as SearchSelectMultipleProps<ValueType>['onChange'])?.(
                nextValues,
                selected,
            );
            return;
        }

        if (nextValue === undefined || nextValue === null) {
            (onChange as SearchSelectSingleProps<ValueType>['onChange'])?.(
                null,
            );
            return;
        }

        const selected = options.find(option => option.value === nextValue);
        (onChange as SearchSelectSingleProps<ValueType>['onChange'])?.(
            nextValue as ValueType,
            selected,
        );
    };

    const resolvedSize =
        size && size in legacySizeMap
            ? legacySizeMap[size as string]
            : (size as AntdSelectProps['size']);

    return (
        <AntdSelect<ValueType | ValueType[]>
            ref={ref}
            allowClear={!hideClearButton}
            filterOption={(input, option) =>
                includesStr(String(option?.label ?? ''), input)
            }
            loading={loading}
            mode={mode}
            notFoundContent={loading ? undefined : 'No options available'}
            onChange={handleChange}
            options={antdOptions}
            placeholder={placeholder}
            showSearch
            size={resolvedSize}
            value={value ?? undefined}
            {...props}
        />
    );
};

const SearchSelect = React.forwardRef(
    SearchSelectInner,
) as SearchSelectComponent;

(SearchSelect as { displayName?: string }).displayName = 'SearchSelect';

export { AntdSelect as AntdSearchSelect };
export default SearchSelect;
